import React, { FormEvent } from 'react';
import typography from '@ds-typography';
import cx from 'classnames';
import { Input } from '@DesignSystem/controllers';
import styles from './SaveTemplatePopover.module.scss';
import { useSelector } from 'react-redux';
import { BasicModal } from '@DesignSystem/modals';
import { useActiveWorkspace } from '@shared/hooks';
import { stringifyJSON } from '@shared/utils/jsonHelpers';
import useCreateCodeTemplate from '@/experiment-management-shared/api/library-panel/useCreateCodeTemplate';
import { NEW_TEMPLATE } from '@/reducers/visualizationsReducer';
import { getChartFormByType } from '@/reducers/dashboardChartsReducer';

interface SaveTemplatePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  chartType: string;
  resetInitialChartState: () => void;
  onEditCustomChart: (chartId: string, libraryTemplateInstance: object) => void;
}

const SaveTemplatePopover: React.FC<SaveTemplatePopoverProps> = ({
  isOpen,
  onClose,
  resetInitialChartState,
  onEditCustomChart,
  chartType
}) => {
  const { mutate, isLoading, error } = useCreateCodeTemplate({
    onClose,
    onEditCustomChart,
    resetInitialChartState
  });

  const [chartName, setChartName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const chartForm = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    getChartFormByType(state, { chartType })
  );

  const workspace = useActiveWorkspace();

  const onSaveBtnClicked = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutate({
      templateName: chartName,
      teamId: workspace?.id,
      code: {
        ...NEW_TEMPLATE.code,
        description,
        code: chartForm.code,
        pyCode: chartForm.code,
        internalResources: [],
        pyConfig: stringifyJSON(NEW_TEMPLATE.code.pyConfig)!,
        defaultConfig: stringifyJSON(NEW_TEMPLATE.code.defaultConfig)!
      },
      scopeType: 'WORKSPACE'
    });
  };

  const renderContent = () => (
    <form onSubmit={onSaveBtnClicked}>
      <div className={styles.body}>
        <div className={cx(typography.dsBody)}>
          The panel will be available in the ‘Panel library’ tab for all team
          members to use across all projects in the workspace.
        </div>
      </div>
      <div className={styles.actions}>
        <Input
          name="chartName"
          height="36px"
          placeholder="Enter name"
          invalidBorder={error}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          /* @ts-ignore */
          invalidMsg={error?.response?.data?.msg}
          required
          title="Panel name"
          value={chartName}
          onChange={setChartName}
          data-test="template-name-input"
        />
        <Input
          name="description"
          height="120px"
          multiline
          lineHeight="1.5"
          value={description}
          placeholder="Enter description"
          title="Description"
          onChange={setDescription}
          data-test="description-input"
        />
      </div>
    </form>
  );

  return (
    <BasicModal
      onClose={onClose}
      open={isOpen}
      additionalClasses={{
        paper: styles.saveTemplatePopoverPaper
      }}
      title="Save to library"
      primaryButtonText="Save"
      className={styles.saveTemplatePopoverContainer}
      isPrimaryButtonDisabled={chartName === ''}
      isPrimaryBtnLoading={isLoading}
      onPrimaryButtonClick={onSaveBtnClicked}
      dataTest="save-template-modal"
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      content={renderContent()}
    />
  );
};

export default SaveTemplatePopover;
